import { createContext, useContext } from 'react';
import type { Bridge } from '@casa/bridge';
import type { CommandInfo, Reading } from '@casa/driver-sdk';
import type { Tile, TileStyle } from '@casa/model';

/**
 * What a tile view needs from the app that shows it: the live readings of the tile's data sources
 * (by the names the tile's options use), their status, a way to send commands to their drivers,
 * the locale for numbers, and the bridge for the video tile's camera. The app provides it with
 * `TileEnvironmentContext`; tile views take it with `useTileEnvironment` and know nothing else of
 * the app.
 *
 * Members named `use…` are React hooks: views call them during render, always in the same order.
 */

export type TileDataStatus = 'ok' | 'waiting' | 'stale' | 'failing';

export type TileCommand = (name: string, arg?: unknown, optimistic?: Reading[] | null) => Promise<void>;

export interface TileEnvironment {
    /** The latest readings of the tile's source `name`; undefined until its driver reports, or for no such source. */
    useReadings(tile: Tile, name: string | undefined) : Reading[] | null | undefined;
    /** `useReadings` for several sources; the list must keep its length. */
    useReadingsList(tile: Tile, names: readonly (string | undefined)[]) : (Reading[] | null | undefined)[];
    /**
     * A function that sends a command to the driver of the tile's source `name`. With `optimistic`,
     * the source shows those readings at once and holds them for a few seconds. The promise rejects
     * when the command fails; the source then shows as failing, with the reason.
     */
    useCommand(tile: Tile, name: string | undefined) : TileCommand;
    /**
     * The commands the tile's source `name` takes, with their arguments, as its driver describes them
     * (a set point's range and step, a thermostat's modes: device-roles.md DR-3). Undefined until the
     * driver answers, and where it cannot: a tile then does without.
     */
    useCommands(tile: Tile, name: string | undefined) : readonly CommandInfo[] | undefined;
    /** The worst status of the tile's sources, and why the failing ones fail. */
    useStatus(tile: Tile) : { status: TileDataStatus; failures: string[] };
    /** BCP 47 locale for numbers (the operating system's regional format); the engine's default without it. */
    readonly locale: string | undefined;
    /** For the video tile's camera connection (`net.tcp`); null where there is no bridge. */
    readonly bridge: Bridge | null;
    /** The dashboard's tile style (dashboard-editor spec §5.9, TS-2); none: the defaults. */
    readonly tileStyle?: TileStyle | undefined;
    /** Shows the dashboard's screen of that name, for a tile's tap (tiles spec TA-2); none: taps go nowhere. */
    readonly showScreen?: ((name: string) => void) | undefined;
}

/** No data at all: tiles show "-" and no camera. Used where no app provides an environment. */
const NO_DATA: TileEnvironment = {
    useReadings: () => undefined,
    useReadingsList: (_tile, names) => names.map(() => undefined),
    useCommand: () => () => Promise.reject(new Error('no data in this view')),
    useCommands: () => undefined,
    useStatus: () => ({ status: 'ok', failures: [] }),
    locale: undefined,
    bridge: null,
};

export const TileEnvironmentContext = createContext<TileEnvironment>(NO_DATA);

export function useTileEnvironment() : TileEnvironment {
    return useContext(TileEnvironmentContext);
}
